import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, Settings, AlertTriangle, Crown, Calendar, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import ThemeToggle from '@/components/ThemeToggle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AppHeader = ({ 
  user, 
  onLogout, 
  showBack = false, 
  backTo = '/admin',
  rightContent = null,
  title = null 
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminDashboard = location.pathname === '/admin';
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [showSubscribeModal, setShowSubscribeModal] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  // Check if user is admin (not therapist or hotel)
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    if (isAdmin) {
      // Check subscription status from localStorage or fetch
      const storedStatus = localStorage.getItem('subscription_status');
      if (storedStatus) {
        setSubscriptionStatus(JSON.parse(storedStatus));
      } else {
        fetchSubscriptionStatus();
      }
      // Fetch company info
      fetchCompanyInfo();
    }
  }, [isAdmin]);

  const fetchCompanyInfo = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tenant/my-company`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompanyInfo(data);
      }
    } catch (error) {
      console.error('Failed to fetch company info');
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/tenant/my-subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubscriptionStatus(data);
        localStorage.setItem('subscription_status', JSON.stringify(data));
      }
    } catch (error) {
      console.error('Failed to fetch subscription status');
    }
  };

  const handleSubscribe = () => {
    navigate('/subscribe');
  };

  // Get company name - use fetched company info or user data
  const getCompanyName = () => {
    return companyInfo?.company_name || user?.company_name || user?.companyName || 'My Business';
  };

  // Calculate days remaining and status
  const getDaysRemaining = () => {
    if (!subscriptionStatus?.ends_at) return null;
    const endDate = new Date(subscriptionStatus.ends_at);
    const now = new Date();
    return Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
  };

  const daysRemaining = getDaysRemaining();
  const isExpired = subscriptionStatus?.is_expired || (daysRemaining !== null && daysRemaining < 0);
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 3 && daysRemaining >= 0;

  return (
    <>
      {/* Subscription Alert Banner - Only for Admin */}
      {isAdmin && subscriptionStatus && (isExpired || isExpiringSoon) && (
        <div className={`px-4 py-2 text-center text-sm ${
          isExpired 
            ? 'bg-red-500/20 border-b border-red-500/30 text-red-300' 
            : 'bg-amber-500/20 border-b border-amber-500/30 text-amber-300'
        }`}>
          <div className="flex items-center justify-center gap-3">
            <AlertTriangle className="w-4 h-4" />
            {isExpired ? (
              <span>Your subscription has expired. Subscribe now to continue using all features.</span>
            ) : (
              <span>Your subscription expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}.</span>
            )}
            <Button 
              size="sm" 
              onClick={handleSubscribe}
              className={`ml-2 h-7 ${
                isExpired 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-amber-500 hover:bg-amber-600 text-white'
              }`}
            >
              <Crown className="w-3 h-3 mr-1" />
              Subscribe Now
            </Button>
          </div>
        </div>
      )}

      <header className="bg-gradient-to-r from-[#1a1512] via-[#2a241f] to-[#1a1512] border-b border-[#B89D62]/30 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left Section: Back button or Logo */}
            <div className="flex items-center gap-4">
              {showBack && (
                <Link to={backTo}>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-[#B89D62] hover:text-[#d4bc7c] hover:bg-[#B89D62]/10"
                    data-testid="back-button"
                  >
                    <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                  </Button>
                </Link>
              )}
              
              <Link to="/admin" className="flex items-center gap-3">
                <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-teal-500 to-emerald-600 flex items-center justify-center shadow-lg">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div className="flex flex-col">
                  <span 
                    className="text-xl font-semibold tracking-wide text-white"
                    data-testid="header-brand-name"
                  >
                    {getCompanyName()}
                  </span>
                  {title && (
                    <span className="text-xs text-slate-400">{title}</span>
                  )}
                </div>
              </Link>
            </div>

            {/* Right Section: Actions */}
            <div className="flex items-center gap-3">
              {/* Subscription Info for Admin */}
              {isAdmin && subscriptionStatus && !isExpired && (
                <div className={`hidden md:flex items-center gap-2 px-3 py-1 rounded-lg text-sm ${
                  subscriptionStatus.status === 'subscribed' || subscriptionStatus.card_captured
                    ? 'bg-emerald-500/10 text-emerald-400'
                    : subscriptionStatus.is_trial 
                      ? 'bg-amber-500/10 text-amber-400'
                      : 'bg-teal-500/10 text-teal-400'
                }`}>
                  <Calendar className="w-4 h-4" />
                  <span>
                    {subscriptionStatus.status === 'subscribed' || subscriptionStatus.card_captured 
                      ? 'Subscribed' 
                      : subscriptionStatus.is_trial 
                        ? 'Trial' 
                        : subscriptionStatus.plan || 'Active'} • 
                    {daysRemaining > 0 ? ` ${daysRemaining}d left` : ' Active'}
                  </span>
                </div>
              )}}
              
              {rightContent}
              
              <ThemeToggle className="text-[#B89D62]" />
              
              {isAdminDashboard && (
                <>
                  {/* Current Package Button */}
                  {isAdmin && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => navigate('/admin/subscription')}
                      className="text-[#B89D62]/80 hover:text-[#B89D62] hover:bg-[#B89D62]/10"
                      data-testid="subscription-button"
                    >
                      <Crown className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      My Plan
                    </Button>
                  )}
                  
                  <Link to="/admin/settings">
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="text-[#B89D62]/80 hover:text-[#B89D62] hover:bg-[#B89D62]/10"
                      data-testid="settings-button"
                    >
                      <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
                      Settings
                    </Button>
                  </Link>
                </>
              )}
              
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onLogout} 
                className="text-[#B89D62]/80 hover:text-[#B89D62] hover:bg-[#B89D62]/10"
                data-testid="logout-button"
              >
                <LogOut className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default AppHeader;
